import { Building2, Plus } from "lucide-react";
import { useState } from "react";

import { ClientEditDialog } from "@/components/client-edit-dialog";
import { Button } from "@/components/ui/button";
import { type ClientSummary, useClients, useProjects } from "@/lib/api";

import { ClientCard } from "./client-card";
import { ClientCreateDialog } from "./client-create-dialog";
import { ClientDeleteDialog } from "./client-delete-dialog";

/**
 * Client management dashboard -- browse every client, edit its brand, delete
 * it (which only unassigns its projects), and expand a card to see the
 * projects grouped under it. Creating a brand-new client also lives here;
 * the new-project flow links back to this page for that.
 */
export function ClientsPage() {
  const { data: clients = [], isLoading: clientsLoading } = useClients();
  const { data: projects = [] } = useProjects();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingClientId, setEditingClientId] = useState<number | null>(null);
  const [deletingClient, setDeletingClient] = useState<ClientSummary | null>(null);

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-primary text-3xl font-normal">Clients</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Manage clients and their brand -- logo, colors, and theme
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 size-4" />
          New Client
        </Button>
      </div>

      {clientsLoading ? (
        <p className="text-muted-foreground py-12 text-center text-sm">Loading clients...</p>
      ) : clients.length === 0 ? (
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <div className="bg-accent/10 rounded-full p-4">
            <Building2 className="text-accent size-8" />
          </div>
          <div>
            <p className="text-primary text-lg font-bold">No clients yet</p>
            <p className="text-muted-foreground mt-1 text-sm">
              Create a client to group projects and share a brand across them.
            </p>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 size-4" />
            New Client
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {clients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              projects={projects.filter((p) => p.client_id === client.id)}
              onEdit={() => setEditingClientId(client.id)}
              onDelete={() => setDeletingClient(client)}
            />
          ))}
        </div>
      )}

      <ClientCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(clientId) => setEditingClientId(clientId)}
      />

      <ClientEditDialog
        clientId={editingClientId}
        open={editingClientId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingClientId(null);
          }
        }}
      />

      <ClientDeleteDialog
        client={deletingClient}
        open={deletingClient !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingClient(null);
          }
        }}
      />
    </div>
  );
}
